// being used
import { getPPERecords } from "../../../service/endpoint-manager";
import moment from "moment";

export const fetchPPERecords = async () => {
  try {
    const response = await getPPERecords({ deleted: false });
    if (response.status === 200) {
      return response.data.body;
    }
    throw new Error("Failed to fetch records");
  } catch (err) {
    throw err;
  }
};

export const calculateComplianceData = (records, strict) => {
  if (!Array.isArray(records)) {
    return [];
  }

  return records.map((record) => {
    const nonCompliantWashRub = record.nonCompliantWashRub || 0;
    const compliant = record.compliantAnswers || 0;
    const total = record.totalQuestions || 0;
    let percentage;

    if (strict) {
      percentage = ((compliant / total) * 100).toFixed(2);
      return {
        numericValue: parseFloat(percentage),
        stringValue: `${percentage}% (${compliant}/${total})`,
      };
    } else {
      const nonStrictCompliance = compliant + nonCompliantWashRub;
      percentage = ((nonStrictCompliance / total) * 100).toFixed(2);
      return {
        numericValue: parseFloat(percentage),
        stringValue: `${percentage}% (${nonStrictCompliance}/${total})`,
      };
    }
  });
};

export const flattenQuestions = (formQuestions) => {
  return formQuestions.flatMap((form) =>
    form.questions.map((q) => {
      if (q.response.outcomeExists) {
        return `${q.response.outcome.type}: ${q.response.outcome.value}`;
      }
      return q.response.value;
    })
  );
};

export const formatCreatedAtDate = (createdAt) => {
  const date = new Date(createdAt);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const calculateSummaryComplianceData = (records, timeFilter, strict) => {
  if (!Array.isArray(records)) {
    return {};
  }

  const groupedData = {};

  records.forEach((record) => {
    const timeUnit =
      timeFilter === "monthly"
        ? moment(record.createdAt).format("MM-YYYY")
        : timeFilter === "quarterly"
        ? moment(record.createdAt).format("Q-YYYY")
        : timeFilter === "yearly"
        ? moment(record.createdAt).format("YYYY")
        : moment(record.createdAt).format("W-YYYY");

    if (!groupedData[timeUnit]) {
      groupedData[timeUnit] = {
        compliant: 0,
        total: 0,
      };
    }

    groupedData[timeUnit].compliant += record.compliantAnswers || 0;
    groupedData[timeUnit].total += record.totalQuestions || 0;
  });

  const complianceData = {};

  Object.keys(groupedData).forEach((timeUnit) => {
    const { compliant, total } = groupedData[timeUnit];
    const percentage =
      total > 0 ? ((compliant / total) * 100).toFixed(2) : "0.00";

    complianceData[timeUnit] = {
      numericValue: parseFloat(percentage),
      stringValue: `${percentage}% (${compliant}/${total})`,
    };
  });

  return complianceData;
};

const groupByWeeks = (records) => {
  const groupedByWeek = {};

  records.forEach((record) => {
    const weekYear = moment(record.createdAt).format("W-YYYY");
    if (!groupedByWeek[weekYear]) {
      groupedByWeek[weekYear] = {
        compliant: 0,
        total: 0,
      };
    }
    groupedByWeek[weekYear].compliant += record.compliantAnswers;
    groupedByWeek[weekYear].total += record.totalQuestions;
  });

  return groupedByWeek;
};

const groupByMonths = (records) => {
  const groupedByMonth = {};

  records.forEach((record) => {
    const monthYear = moment(record.createdAt).format("MM-YYYY");
    if (!groupedByMonth[monthYear]) {
      groupedByMonth[monthYear] = {
        compliant: 0,
        total: 0,
      };
    }
    groupedByMonth[monthYear].compliant += record.compliantAnswers;
    groupedByMonth[monthYear].total += record.totalQuestions;
  });
  return groupedByMonth;
};
const getUniqueMonths = (ppeRecords) => {
  const monthSet = new Set(
    ppeRecords.map((record) => moment(record.createdAt).format("MM-YYYY"))
  );
  return Array.from(monthSet).sort((a, b) =>
    moment(a, "MM-YYYY").diff(moment(b, "MM-YYYY"))
  );
};

const groupByQuarters = (records) => {
  const groupedByQuarter = {};

  records.forEach((record) => {
    const quarterYear = moment(record.createdAt).format("Q-YYYY");
    if (!groupedByQuarter[quarterYear]) {
      groupedByQuarter[quarterYear] = {
        compliant: 0,
        total: 0,
      };
    }
    groupedByQuarter[quarterYear].compliant += record.compliantAnswers;
    groupedByQuarter[quarterYear].total += record.totalQuestions;
  });

  return groupedByQuarter;
};

const groupByYears = (records) => {
  const groupedByYear = {};

  records.forEach((record) => {
    const year = moment(record.createdAt).format("YYYY");
    if (!groupedByYear[year]) {
      groupedByYear[year] = {
        compliant: 0,
        total: 0,
      };
    }
    groupedByYear[year].compliant += record.compliantAnswers;
    groupedByYear[year].total += record.totalQuestions;
  });

  return groupedByYear;
};

export const calculateWeeklyComplianceData = (records) => {
  return calculateSummaryComplianceData(records, (createdAt) =>
    moment(createdAt).format("W-YYYY")
  );
};

export const calculateMonthlyComplianceData = (records) => {
  return calculateSummaryComplianceData(records, (createdAt) =>
    moment(createdAt).format("MM-YYYY")
  );
};

export const calculateQuarterlyComplianceData = (records) => {
  return calculateSummaryComplianceData(records, (createdAt) =>
    moment(createdAt).format("Q-YYYY")
  );
};

export const calculateYearlyComplianceData = (records) => {
  return calculateSummaryComplianceData(records, (createdAt) =>
    moment(createdAt).format("YYYY")
  );
};
